import React, { createContext, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import mixpanel, {
  initMixpanel,
  persistUTMParams,
  trackAppViewed,
  trackSessionStarted,
  trackEvent,
} from "../utils/mixpanel";

// Create context
export const MixpanelContext = createContext(null);

/**
 * MixpanelProvider - Initializes Mixpanel and provides automatic tracking
 *
 * Features:
 * - Initializes Mixpanel on app load
 * - Tracks "App Viewed" on first load
 * - Tracks "Session Started" on first interaction
 * - Automatically tracks page views on route changes
 * - Persists UTM parameters
 */
export const MixpanelProvider = ({ children }) => {
  const location = useLocation();
  const hasTrackedAppViewed = useRef(false);
  const hasTrackedSessionStarted = useRef(false);
  const previousPath = useRef(null);

  // Initialize Mixpanel on mount
  useEffect(() => {
    // Initialize Mixpanel SDK
    initMixpanel();

    // Persist UTM parameters from URL
    persistUTMParams();

    // Track App Viewed (only once per session)
    if (!hasTrackedAppViewed.current) {
      // Small delay to ensure mixpanel_data is loaded from NewFooter
      const timer = setTimeout(() => {
        trackAppViewed();
        hasTrackedAppViewed.current = true;
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  // Track Session Started on first user interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!hasTrackedSessionStarted.current) {
        trackSessionStarted();
        hasTrackedSessionStarted.current = true;

        // Remove listeners after first interaction
        document.removeEventListener("click", handleFirstInteraction);
        document.removeEventListener("touchstart", handleFirstInteraction);
        document.removeEventListener("scroll", handleFirstInteraction);
      }
    };

    document.addEventListener("click", handleFirstInteraction);
    document.addEventListener("touchstart", handleFirstInteraction);
    document.addEventListener("scroll", handleFirstInteraction);

    return () => {
      document.removeEventListener("click", handleFirstInteraction);
      document.removeEventListener("touchstart", handleFirstInteraction);
      document.removeEventListener("scroll", handleFirstInteraction);
    };
  }, []);

  // Track page views on route changes
  useEffect(() => {
    const currentPath = location.pathname;

    // Only track if path actually changed
    if (previousPath.current !== currentPath) {
      // Extract page info from path
      const pathParts = currentPath.split("/").filter(Boolean);
      const roomUuid = pathParts[0] || "";
      const pageName = pathParts.slice(1).join("/") || "home";

      // Map common paths to readable page names
      const pageNameMap = {
        "qr-code": "Home",
        menu: "Menu",
        cart: "Cart",
        search: "Search",
        checkout: "Checkout",
        "order-status": "Order Status",
        restaurant: "Restaurant",
        spa: "Spa",
        laundry: "Laundry",
        pharmacy: "Pharmacy",
        taxi: "Taxi",
        flowers: "Flowers",
      };

      // Get readable page name
      const readablePageName =
        pageNameMap[pageName.toLowerCase()] ||
        pageName
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

      // Track page view (but not for the initial load - App Viewed handles that)
      // if (previousPath.current !== null) {
      //   trackEvent('Page Viewed', {
      //     page_name: readablePageName,
      //     page_path: currentPath,
      //     previous_page: previousPath.current,
      //   });
      // }

      previousPath.current = currentPath;
    }
  }, [location.pathname]);

  // Context value with mixpanel instance
  const contextValue = {
    mixpanel,
    trackEvent,
  };

  return (
    <MixpanelContext.Provider value={contextValue}>
      {children}
    </MixpanelContext.Provider>
  );
};

/**
 * Hook to access Mixpanel context directly
 * For most cases, use useMixpanel hook instead
 */
export const useMixpanelContext = () => {
  const context = useContext(MixpanelContext);
  if (!context) {
    console.warn("useMixpanelContext must be used within MixpanelProvider");
  }
  return context;
};

export default MixpanelProvider;
